import { useFormikContext } from "formik";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { ReDeclareType } from "csv-package";

import Button from "../components/Button";
import Modal from "../components/Modal";
import api from "../helpers/api";
import ApplicationContext from "../reducers/applicationFrame";
import { FlatApplciationType } from "../types";
import { hasAnonymousIdentity, removeAnonymousIdentity } from "../helpers/auth";

export type NetworkStatusType =
  | "uninitialised"
  | "loading"
  | "successful"
  | "failed";

export type ReDeclareErrorType = {
  title: string;
  body: string;
};

export type ReDecalreLocalState = {
  call_back_status: NetworkStatusType;
  submit_status: NetworkStatusType;
  declaration_of_truth: boolean;
  errors: ReDeclareErrorType | null;
};

type ReDeclareControls = {
  reDeclareState: ReDecalreLocalState;
  handleCallBack: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void;
  handleAcceptReDeclare: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void;
  handleDeclarationOfTruth: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleErrorModalClose: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void;
  reDeclareObject: ReDeclareType;
};

const useReDeclare = (): ReDeclareControls => {
  const { id: ApplicationID } = useParams<{ id: string }>();
  const { values, setFieldValue } = useFormikContext<FlatApplciationType>();
  const {
    goToStep,
    state: { currentStep },
  } = React.useContext(ApplicationContext);

  const [
    reDeclareState,
    setReDeclareState,
  ] = React.useState<ReDecalreLocalState>({
    declaration_of_truth: values.Status === "re-declare-accepted",
    call_back_status: "uninitialised", // uninitialised | loading | successful | failed
    submit_status: "uninitialised", // uninitialised | loading | successful | failed
    errors: null,
  });

  // get latest re-declare object
  const reDeclareObject = values.ReDeclarations.sort(
    (a: ReDeclareType, b: ReDeclareType) => {
      const aDate = new Date(a.Created);
      const bDate = new Date(b.Created);
      return aDate.valueOf() - bDate.valueOf();
    }
  )[values.ReDeclarations.length - 1];

  // update state on load
  // this will lock the view
  React.useEffect(() => {
    if (values.Flags.call_back) {
      setReDeclareState((s) => ({
        ...s,
        call_back_status: "successful",
      }));
    }
  }, [values.Flags.call_back]);

  const handleCallBack = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => {
      e.preventDefault();
      try {
        console.log('=== RE-DECLARE CALLBACK DEBUG ===');
        console.log('ApplicationID:', ApplicationID);
        console.log('Current values:', values);
        console.log('ReDeclareState:', reDeclareState);
        
        setReDeclareState((s) => ({
          ...s,
          call_back_status: "loading",
        }));
        
        const payload = { ApplicationID };
        console.log('Callback API payload:', payload);
        
        await api("post", "/public/reDeclare/requestCallback", payload);
        
        console.log('Callback API call successful');
        
        setReDeclareState((s) => ({
          ...s,
          call_back_status: "successful",
        }));
      } catch (e: any) {
        const timestamp = new Date().toISOString();
        console.error('=== RE-DECLARE CALLBACK ERROR ===');
        console.error('Timestamp:', timestamp);
        console.error('ApplicationID:', ApplicationID);
        console.error('Error:', e);
        console.error('Error details:', {
          message: e.message,
          status: e.response?.status,
          statusText: e.response?.statusText,
          data: e.response?.data,
          config: e.config,
          url: e.config?.url,
          method: e.config?.method,
          headers: e.config?.headers,
          requestId: e.response?.headers?.['x-amzn-requestid'] || e.response?.headers?.['x-amz-request-id'],
          cfRay: e.response?.headers?.['cf-ray'],
          timestamp
        });

        // body:
        // `Please try again, if this problem persists please contact your registrar.\n\n` +
        // `Timestamp: ${timestamp}\n` +
        // `ApplicationID: ${ApplicationID}\n` +
        // `Error: ${e.message}\n` +
        // `Status: ${e.response?.status || 'Unknown'}\n` +
        // `Request ID: ${e.response?.headers?.['x-amzn-requestid'] || e.response?.headers?.['x-amz-request-id'] || 'Unknown'}\n` +
        // `URL: ${e.config?.url || 'Unknown'}\n` +
        // `Full Error: ${JSON.stringify(e, null, 2)}`,
        
        setReDeclareState((s) => ({
          ...s,
          call_back_status: "failed",
          errors: {
            title: "Re-declare request failed (callback)",
            body: "Please try again, if this problem persists please contact your registrar."
          },
        }));
      }
    },
    [ApplicationID, values, reDeclareState]
  );

  const handleAcceptReDeclare = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => {
      e.preventDefault();
      if (reDeclareState.declaration_of_truth === false) {
        setReDeclareState((s) => ({
          ...s,
          errors: {
            title: "Error: missing declaration of truth",
            body: "You must agree with the declaration of truth",
          },
        }));
        return;
      }
      try {
        console.log('=== RE-DECLARE ACCEPT DEBUG ===');
        console.log('ApplicationID:', values.ApplicationID);
        console.log('ReDeclareKey:', reDeclareObject.ReDeclareKey);
        console.log('ReDeclareObject:', reDeclareObject);
        console.log('All ReDeclarations:', values.ReDeclarations);
        console.log('Current values:', values);
        console.log('ReDeclareState:', reDeclareState);
        
        setReDeclareState((s) => ({
          ...s,
          submit_status: "loading",
        }));
        
        type R = {
          status: "ok" | "failed";
        };
        
        const payload = {
          ApplicationID: values.ApplicationID,
          ReDeclareKey: reDeclareObject.ReDeclareKey,
        };
        
        console.log('Accept API payload:', payload);
        console.log('API endpoint: /public/reDeclare/acceptReDeclare');
        
        const result = (await api("post", "/public/reDeclare/acceptReDeclare", payload)) as R;
        
        console.log('Accept API response:', result);

        if (result.status === "ok") {
          console.log('Accept API call successful, updating status to re-declare-accepted');
          
          setFieldValue("Status", "re-declare-accepted");

          setReDeclareState((s) => ({
            ...s,
            submit_status: "successful",
          }));

          const nextStep = currentStep + 1;
          console.log('Moving to next step:', nextStep);

          // send user to complete page.
          goToStep(nextStep);
        }
      } catch (e: any) {
        const timestamp = new Date().toISOString();
        console.error('=== RE-DECLARE ACCEPT ERROR ===');
        console.error('Timestamp:', timestamp);
        console.error('ApplicationID:', values.ApplicationID);
        console.error('ReDeclareKey:', reDeclareObject.ReDeclareKey);
        console.error('Error:', e);
        console.error('Error details:', {
          message: e.message,
          status: e.response?.status,
          statusText: e.response?.statusText,
          data: e.response?.data,
          config: e.config,
          url: e.config?.url,
          method: e.config?.method,
          headers: e.config?.headers,
          requestId: e.response?.headers?.['x-amzn-requestid'] || e.response?.headers?.['x-amz-request-id'],
          cfRay: e.response?.headers?.['cf-ray'],
          timestamp,
          payload: {
            ApplicationID: values.ApplicationID,
            ReDeclareKey: reDeclareObject.ReDeclareKey,
          }
        });

        // body:
        //   `Please try again, if this problem persists please contact your registrar.\n\n` +
        //   `Timestamp: ${timestamp}\n` +
        //   `ApplicationID: ${values.ApplicationID}\n` +
        //   `ReDeclareKey: ${reDeclareObject.ReDeclareKey}\n` +
        //   `Error: ${e.message}\n` +
        //   `Status: ${e.response?.status || 'Unknown'}\n` +
        //   `Request ID: ${e.response?.headers?.['x-amzn-requestid'] || e.response?.headers?.['x-amz-request-id'] || 'Unknown'}\n` +
        //   `URL: ${e.config?.url || 'Unknown'}\n` +
        //   `Full Error: ${JSON.stringify(e, null, 2)}`,
        
        setReDeclareState((s) => ({
          ...s,
          submit_status: "failed",
          errors: {
            title: "Re-declare request failed (accept)",
            body: "Please try again, if this problem persists please contact your registrar."
          },
        }));
      }
    },
    [reDeclareState, values, reDeclareObject, setFieldValue, currentStep, goToStep]
  );

  const handleDeclarationOfTruth = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setReDeclareState((s) => ({
        ...s,
        declaration_of_truth: !s.declaration_of_truth,
      }));
    },
    []
  );

  const handleErrorModalClose = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => {
      e.preventDefault();
      setReDeclareState((s) => ({
        ...s,
        errors: null,
      }));
    },
    []
  );

  return {
    reDeclareState,
    handleCallBack,
    handleAcceptReDeclare,
    reDeclareObject,
    handleDeclarationOfTruth,
    handleErrorModalClose,
  };
};

export default useReDeclare;

export const ReDeclareModals: React.FC<{
  reDeclareState: ReDecalreLocalState;
  handleErrorModalClose: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<Element>) => void;
}> = ({ reDeclareState, handleErrorModalClose }) => {
  const { push } = useHistory();

  const { errors } = reDeclareState;
  if (errors !== null) {
    return (
      <Modal>
        <h2>{errors.title}</h2>
        <p>{errors.body}</p>
        <footer className="buttons">
          <Button
            variant="alert"
            action={handleErrorModalClose}
          >
            Continue
          </Button>
        </footer>
      </Modal>
    );
  } else if (reDeclareState.call_back_status === "successful") {
    return (
      <Modal>
        <h2>Call back request successful</h2>
        <p>
          A court registrar will contact you using your safe contact details. If
          your details have changed, please contact your{" "}
          <a
            href="https://mcv.vic.gov.au/going-court/find-court"
            target="_blank"
            rel="noreferrer"
          >
            local court.
          </a>
        </p>
        <footer className="buttons">
          <Button
            variant="alert"
            action={(e) => {
              e.preventDefault();
              push("/quick");
            }}
          >
            QuickExit
          </Button>
          {!hasAnonymousIdentity() && (
          <Button
            action={(e) => {
              e.preventDefault();
              push("/applications");
            }}
          >
            Back to applications
          </Button>)}
          <Button
            action={async (e) => {
              e.preventDefault();
              await removeAnonymousIdentity()
              push("/");
            }}
          >
            Close application and go to home page
          </Button>
        </footer>
      </Modal>
    );
  }
  return null;
};
